import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Editor from '@monaco-editor/react';
import _ from 'lodash';
import { useResizeDetector } from 'react-resize-detector';
import { Button, Modal, ModalVariant, Banner, Stack, StackItem, CodeBlock } from '@patternfly/react-core';

import { ModelPropType } from '../ModelPropTypes';
import useBoolean from 'utils/hooks/useBoolean';
import { saveButtons } from 'components/Modals/ModalButtons';
import { useTranslation } from 'localization/useTranslation';
import ViewModal from 'components/Modals/ViewModal';

const JsonModalEditorComponent = ({ model, value, onChange }) => {
  const T = useTranslation();
  const [isEditorOpen, openEditor, closeEditor] = useBoolean(false);
  const monaco = useRef();
  const [saveEnabled, setSaveEnabled] = useState(true);
  const [banner, setBanner] = useState('green');
  const [bannerMessage, setBannerMessage] = useState('');
  const [viewErrorMessage, setViewErrorMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const { height, ref } = useResizeDetector();


  const _onSave = useCallback(() => {
    closeEditor();
    const editorData = JSON.parse(monaco.current.getValue());
    onChange(model, editorData);
  }, [closeEditor, model, onChange]);

  const _onChange = useCallback((evt) => {
    try {
      JSON.parse(evt);
      setBanner('green');
      setSaveEnabled(true);
    } catch (error) {
      setBanner('gold');
      setSaveEnabled(false);
    }
  }, []);

  const handleEditorValidation = useCallback((markers) => {
    if (_.isEmpty(markers)) {
      setBanner('green');
      setBannerMessage('');
    } else {
      setBanner('red');
      setBannerMessage('Invalid JSON, click to view more details.');
      setErrorMessage(<CodeBlock>{ _.map(markers, marker => `Line ${marker.endLineNumber} : ${marker.message}`) }</CodeBlock>);
    }
  }, []);

  const onBannerClick = useCallback(() => {
    setViewErrorMessage(errorMessage);
  }, [errorMessage]);

  const handleEditorDidMount = useCallback((editor) => {
    monaco.current = editor;
  }, []);

  return (
    <>
      <Button onClick={openEditor}>Open Editor</Button>
      <Modal
        variant={ModalVariant.large}
        title="Json Editor"
        isOpen={isEditorOpen}
        onClose={closeEditor}
        appendTo={() => document.body}
        actions={saveButtons({
          T,
          onSave: _onSave,
          saveEnabled,
          closeEditor
        })}
      >
        <Stack>
          <StackItem>
            <Banner style={{ height: '30px' }} variant={banner} onClick={() => onBannerClick()}>
              { bannerMessage }
            </Banner>
          </StackItem>
          <StackItem isFilled style={{
            height: model.properties?.container?.height || '400px',
            width: model.properties?.container?.width || ''
          }}>
            <div ref={ref} style={{ height: '100%' }}>
              <Editor
                height={height ? height - 50 : undefined}
                defaultLanguage="json"
                value={JSON.stringify(value ?? model.properties?.initialValue, null, 2)}
                onChange={_onChange}
                onValidate={handleEditorValidation}
                onMount={handleEditorDidMount}
              />
            </div>
          </StackItem>
        </Stack>
      </Modal>
      <ViewModal
        title="View Error Message"
        onClose={() => setViewErrorMessage(null)}
        Component={viewErrorMessage}
      />
    </>
  );
};

JsonModalEditorComponent.propTypes = {
  model: ModelPropType,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired
};

export default React.memo(JsonModalEditorComponent);
